/**
 * TravelSystem.ts — World navigation and travel mechanics.
 *
 * Manages movement between locations, frostbite exposure during travel,
 * and random encounter chances based on route danger level.
 */

import type { GameState } from '@core/engine/GameState';
import type { EventSystem } from '@core/engine/EventSystem';
import {
  getConnections,
  getConnection,
  type LocationConnection,
} from '@game/data/location-graph';
import { FogOfWarState } from '@game/world/FogOfWarState';

// ─── Types ──────────────────────────────────────────────────────

export interface TravelResult {
  from: string;
  to: string;
  connection: LocationConnection;
  frostbiteGained: number;
  randomEncounter: boolean;
  discovered: boolean;
}

// ─── TravelSystem ───────────────────────────────────────────────

export class TravelSystem {
  private events: EventSystem;
  fogOfWar: FogOfWarState;

  constructor(events: EventSystem) {
    this.events = events;
    this.fogOfWar = new FogOfWarState();
  }

  /** Set fog of war state (after loading a save). */
  setFogOfWar(fog: FogOfWarState): void {
    this.fogOfWar = fog;
  }

  /**
   * Get available travel destinations from the player's current location.
   * Only returns discovered locations (fog of war).
   */
  getAvailableDestinations(state: GameState): LocationConnection[] {
    const currentLocation = state.currentLocationId;
    if (!currentLocation) return [];

    return getConnections(currentLocation).filter(
      conn => this.fogOfWar.isDiscovered(conn.to),
    );
  }

  /**
   * Get all connections from current location (including undiscovered).
   * Used for map display with fog of war.
   */
  getAllConnections(state: GameState): LocationConnection[] {
    const currentLocation = state.currentLocationId;
    if (!currentLocation) return [];
    return getConnections(currentLocation);
  }

  /**
   * Execute travel between two locations.
   * Applies frostbite exposure and checks for random encounters.
   */
  travel(from: string, to: string, state: GameState): TravelResult | null {
    const connection = getConnection(from, to);
    if (!connection) return null;

    // Calculate frostbite with skill reduction
    const frostbiteGained = Math.max(0, connection.frostbiteExposure);
    state.character.frostbite = Math.min(100, state.character.frostbite + frostbiteGained);

    // Update current location
    state.currentLocationId = to;

    // Discover destination
    const discovered = this.fogOfWar.discover(to);

    // Random encounter check
    const encounterChance = this.getEncounterChance(connection.danger);
    const randomEncounter = Math.random() < encounterChance;

    const result: TravelResult = {
      from,
      to,
      connection,
      frostbiteGained,
      randomEncounter,
      discovered,
    };

    this.events.emit('traveled', result);

    return result;
  }

  /**
   * Discover a location without traveling to it (story event, NPC tip, etc.).
   */
  revealLocation(locationId: string): boolean {
    const discovered = this.fogOfWar.discover(locationId);
    if (discovered) {
      this.events.emit('locationDiscovered', { locationId });
    }
    return discovered;
  }

  /**
   * Initialize starting location and discover it + neighbors.
   */
  initializeStartingLocation(locationId: string, state: GameState): void {
    state.currentLocationId = locationId;
    this.fogOfWar.discover(locationId);

    // Also discover directly connected locations
    const connections = getConnections(locationId);
    for (const conn of connections) {
      this.fogOfWar.discover(conn.to);
    }
  }

  // ─── Private ──────────────────────────────────────────────

  private getEncounterChance(danger: string): number {
    switch (danger) {
      case 'safe': return 0;
      case 'moderate': return 0.15;
      case 'dangerous': return 0.3;
      case 'deadly': return 0.5;
      default: return 0.1;
    }
  }
}
